import React, { lazy, Suspense, useContext, useEffect } from "react";
import { Router, Switch, Route, Redirect } from "react-router-dom";
import history from "./../src/history";
import { connect } from "react-redux";
import { ApolloProvider } from "@apollo/client";
import client from "./client";
import Dashboard from "components/Dashboard";
import Login from "pages/Login";
import Sso from "pages/Sso";
import ForgotPassword from "pages/ForgotPassword";
import ResetPassword from "pages/ResetPassword";
import UsersContainer from "pages/Users/UsersContainer";
import UsersUpdateContainer from "pages/Users/UserUpdateContainer";
import UserCreateContainer from "pages/Users/UserCreateContainer";
import SourceCreateContainer from "pages/SourcePage/SourceCreateContainer";
import SourceUpdateContainer from "pages/SourcePage/SourceUpdateContainer";
import QueryBuilder from "pages/QueryBuilder";
import { getCookie } from "helpers/Cookie";
import { AppContext, AppContextProvider } from "contexts/AppContextProvider";
import Nav from "components/Nav/Nav";
import SourcesMasterPage from "pages/SourcePage/SourcesMasterPage";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import Loader from "components/Loader/Loader";
import { reidentifyUser } from "./utils/analytics";

// Suppress Recharts defaultProps warning
const originalError = console.error;
console.error = (...args) => {
  if (
    args[0] &&
    typeof args[0] === "string" &&
    args[0].includes("defaultProps will be removed")
  ) {
    return;
  }
  originalError.apply(console, args);
};

export const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

/**
 * Remap a route object by setting the key having the path as value
 * @param {*} route
 */
const remapRoutes = (route) => ({ ...route, key: route.props.path });

/**
 * Group Catalog routes
 */
const CatalogRoutes = () =>
  [<Route path="/settings/catalog" component={SourcesMasterPage} />].map(
    remapRoutes
  );

/**
 * Group QB routes
 */
const QueryBuilderRoutes = () =>
  [<Route path="/:tenant/query" component={QueryBuilder} />].map(remapRoutes);

/**
 * Loads MixBuilder on demand
 * @returns
 */
const MixLoader = () => {
  const { currentProject } = useContext(AppContext).state;
  if (currentProject?.projectObj?.nav?.props?.mix !== true) {
    return null;
  } else {
    const MixBuilder = lazy(() => import("pages/mix/MixBuilder"));

    return (
      <Suspense
        fallback={
          <Loader minHeight={370} col={"col-12"} size="circle-scale--sm" />
        }
      >
        <MixBuilder />
      </Suspense>
    );
  }
};

/**
 * Group Sources CRUD routes
 */
const SourcesRoutes = () =>
  [
    <Route path="/settings/sources" component={SourcesMasterPage} />,
    <Route path="/settings/source/create" component={SourceCreateContainer} />,
    <Route
      path="/settings/source/:name/:id"
      component={SourceUpdateContainer}
    />,
  ].map(remapRoutes);

/**
 * Group Users CRUD routes
 */
const UsersRoutes = () =>
  [
    <Route path="/settings/users" component={UsersContainer} />,
    <Route path="/settings/user/create" component={UserCreateContainer} />,
    <Route path="/settings/user/:email" component={UsersUpdateContainer} />,
  ].map(remapRoutes);

/**
 * Group Mix routes
 */
const MixRoutes = () =>
  [<Route path="/:tenant/mix" component={MixLoader} />].map(remapRoutes);

const Main = () => {
  useEffect(() => {
    // Re-identify user on app initialization
    reidentifyUser();
  }, []);

  return (
    <ApolloProvider client={client}>
      <AppContextProvider>
        <QueryClientProvider client={queryClient}>
          <Nav />
          <Switch>
            <Route exact path="/" component={Dashboard} />
            {SourcesRoutes()}
            {CatalogRoutes()}
            {UsersRoutes()}
            {QueryBuilderRoutes()}
            {MixRoutes()}
            <Route path="/:board/:page?" component={Dashboard} />
          </Switch>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AppContextProvider>
    </ApolloProvider>
  );
};

const App = ({ token }) => (
  <Router history={history}>
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/sso" component={Sso} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/password_reset/token/:token" component={ResetPassword} />
      <Authenticated path="/" component={Main} />
    </Switch>
  </Router>
);

const mapStateToProps = (state) => {
  const getItem = window.localStorage.getItem.bind(window.localStorage);

  return {
    token:
      // token from state
      state.auth ||
      // token from cookie after Twitter/FB authentication (connector redirect)
      getCookie("token") ||
      // token from current session storage
      getItem("token"),
  };
};

const Authenticated = connect(mapStateToProps)(({
  component: Component,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={(props) => {
        if (rest.token) {
          return <Component {...props} />;
        }
        return (
          <Redirect
            to={{
              pathname: "/login",
              state: { nextPathname: props.location },
            }}
          />
        );
      }}
    />
  );
});

export default App;
